import React, { useMemo } from 'react';
import UserMessage from './UserMessage';
import AssistantMessage from './AssistantMessage';

function MessageList({
  messages,
  status,
  contentType,
  contentId,
  onShareChat,
  onSharePrompt,
  conversationId,
  onUploadComplete,
  chatData,
}: {
  messages: any[];
  status: string;
  contentType?: string;
  contentId?: string;
  onShareChat?: () => void;
  onSharePrompt?: () => void;
  conversationId?: string;
  onUploadComplete?: any;
  chatData?: any;
}) {
  const lastMessage = messages.length > 0 ? messages[messages.length - 1] : null;
  // The last assistant turn belongs to `StableMessageList` only once the run reaches `ready`.
  // That list hardcodes `status="ready"` and `isLast={false}`, so anything it holds shows its
  // action row unconditionally — and the SDK appends the assistant message while the status is
  // still `submitted`, which flashed rate/copy/share for a frame before streaming began. `error`
  // is deliberately on this side of the line too: a failed turn never produced a response to act
  // on. Checking for `ready` rather than listing the in-flight statuses means a status this code
  // has not seen before fails safe.
  const hasUnfinishedAssistantMessage = status !== 'ready' && !!lastMessage && lastMessage.role === 'assistant';

  const stableMessages = useMemo(() => {
    if (hasUnfinishedAssistantMessage && messages.length > 0) {
      return messages.slice(0, -1);
    }
    return messages;
  }, [messages, hasUnfinishedAssistantMessage]);

  // Check if shareParams exists
  const hasShareParams = useMemo(() => {
    return !!chatData?.chat?.shareParams;
  }, [chatData?.chat?.shareParams]);

  // Find first user message index for comparison
  const firstUserMessageIndex = useMemo(() => {
    return messages.findIndex((msg: any) => msg.role === 'user');
  }, [messages]);

  const streamingMessage = useMemo(() => {
    if (hasUnfinishedAssistantMessage && messages.length > 0) {
      return messages[messages.length - 1];
    }
    return null;
  }, [messages, hasUnfinishedAssistantMessage]);

  // Stands in for the assistant turn until the SDK appends the real one. Once that message exists
  // it renders its own in-flight header, so keeping the placeholder as well would put two
  // "Thinking" indicators on screen and reserve a row that nothing ever fills.
  const loadingMessage = useMemo(() => {
    if (status === 'submitted' && !hasUnfinishedAssistantMessage) {
      return {
        id: 'loading-message',
        role: 'assistant',
        content: '',
        isLoading: true,
      };
    }
    return null;
  }, [status, hasUnfinishedAssistantMessage]);

  return (
    <>
      {/* Stable messages */}
      {stableMessages.length > 0 && (
        <StableMessageList
          messages={stableMessages}
          contentType={contentType}
          contentId={contentId}
          onShareChat={onShareChat}
          onSharePrompt={onSharePrompt}
          conversationId={conversationId}
          onUploadComplete={onUploadComplete}
          hasShareParams={hasShareParams}
          firstUserMessageIndex={firstUserMessageIndex}
        />
      )}

      {/* Streaming message */}
      {streamingMessage && (
        <StreamingMessage
          message={streamingMessage}
          status={status}
          contentType={contentType}
          contentId={contentId}
          onShareChat={onShareChat}
          conversationId={conversationId}
          onUploadComplete={onUploadComplete}
        />
      )}

      {/* Loading message */}
      {loadingMessage && (
        <StreamingMessage
          message={loadingMessage}
          status={status}
          contentType={contentType}
          contentId={contentId}
          onShareChat={onShareChat}
          conversationId={conversationId}
          onUploadComplete={onUploadComplete}
        />
      )}
    </>
  );
}

// Memoized stable message list
const StableMessageList = React.memo(
  function StableMessageList({
    messages,
    contentType,
    contentId,
    onShareChat,
    onSharePrompt,
    conversationId,
    onUploadComplete,
    hasShareParams,
    firstUserMessageIndex,
  }: {
    messages: any[];
    contentType?: string;
    contentId?: string;
    onShareChat?: () => void;
    onSharePrompt?: () => void;
    conversationId?: string;
    onUploadComplete?: any;
    hasShareParams?: boolean;
    firstUserMessageIndex?: number;
  }) {
    return (
      <>
        {messages.map((message, index) => {
          if (message.role === 'user') {
            const isFirstUserMessage = index === firstUserMessageIndex;
            return (
              <UserMessage
                key={`user-${message.id}`}
                message={message}
                contentType={contentType}
                contentId={contentId}
                onSharePrompt={onSharePrompt}
                isFirstUserMessage={isFirstUserMessage}
                hasShareParams={hasShareParams}
              />
            );
          } else if (message.role === 'assistant') {
            return (
              <AssistantMessage
                key={`assistant-${message.id}`}
                message={message}
                status="ready"
                isLast={false}
                onShareChat={onShareChat}
                conversationId={conversationId}
                onUploadComplete={onUploadComplete}
              />
            );
          }
          return null;
        })}
      </>
    );
  },
  // A custom comparator is required: `messages` is a fresh array on every throttled stream tick,
  // so the default shallow compare would re-render the whole list ~20x/s. But it compares message
  // *identity*, not just ids — while the assistant streams, this slice keeps a constant length and
  // constant ids, so an id-only compare froze the entire list and silently dropped any
  // `setMessages` patch to an existing message (a slow upload resolving its file part URLs showed
  // up as an attachment vanishing when the assistant replied). Message objects are recreated on
  // every real change, so a reference check is exact and O(n) cheap.
  (prevProps, nextProps) => {
    if (prevProps.messages.length !== nextProps.messages.length) {
      return false;
    }

    for (let i = 0; i < prevProps.messages.length; i++) {
      if (prevProps.messages[i] !== nextProps.messages[i]) {
        return false;
      }
    }

    return (
      prevProps.contentType === nextProps.contentType &&
      prevProps.contentId === nextProps.contentId &&
      prevProps.hasShareParams === nextProps.hasShareParams &&
      prevProps.firstUserMessageIndex === nextProps.firstUserMessageIndex &&
      prevProps.conversationId === nextProps.conversationId &&
      prevProps.onShareChat === nextProps.onShareChat &&
      prevProps.onSharePrompt === nextProps.onSharePrompt &&
      prevProps.onUploadComplete === nextProps.onUploadComplete
    );
  }
);

// Component for the streaming message
function StreamingMessage({
  message,
  status,
  contentType,
  contentId,
  onShareChat,
  conversationId,
  onUploadComplete,
}: {
  message: any;
  status: string;
  contentType?: string;
  contentId?: string;
  onShareChat?: () => void;
  conversationId?: string;
  onUploadComplete?: any;
}) {
  if (message.role === 'user') {
    return <UserMessage key={`user-${message.id}`} message={message} contentType={contentType} contentId={contentId} />;
  } else if (message.role === 'assistant') {
    return (
      <AssistantMessage
        key={`assistant-${message.id}`}
        message={message}
        status={status}
        isLast={true}
        onShareChat={onShareChat}
        conversationId={conversationId}
        onUploadComplete={onUploadComplete}
      />
    );
  }

  return null;
}

export default MessageList;
